import { useState, useRef, useCallback, useEffect } from 'react';

interface IUseInfiniteScroll<T> {
  fetchMore?: () => Promise<T[]>;
  from?: number;
  pageSize?: number;
  rows: T[];
}

const useInfiniteScroll = <T,>({ fetchMore, rows }: IUseInfiniteScroll<T>) => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setresults] = useState<T[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);

  const onScrollEnd = async () => {
    const data = await fetchMore?.();
    return data || [];
  };

  const process = () => {
    setIsLoading(true);
    onScrollEnd()
      .then((data) => {
        setHasNextPage(Boolean(data.length));
        setresults((prev) => [...prev, ...data]);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log('fetch error ', error);
        setHasNextPage(false);
        setIsLoading(false);
      });
  };

  const intObserver = useRef<IntersectionObserver>();
  const lastItemRef = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (item: any) => {
      if (isLoading) return;
      if (intObserver.current) intObserver.current.disconnect();
      intObserver.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          process();
        }
      });
      if (item) intObserver.current.observe(item);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading, hasNextPage],
  );

  useEffect(() => {
    setresults(rows);
    setHasNextPage(true);
  }, [rows]);

  return { lastItemRef, results, isLoading };
};

export default useInfiniteScroll;
